import React, { useState } from 'react';
import { Button, Paper, TextField, Typography, Box, Checkbox, FormControlLabel, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { persistAuthSession } from '../helpers/authSession';
import RegistrationModal from '../components/RegistrationModal';

function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        try {
            const response = await api.post('auth/login', {
                email,
                password,
                rememberMe
            });
            persistAuthSession(response.data);
            navigate('/admin');
        } catch (err) {
            setError('Credenciais inválidas. Por favor, tente novamente.');
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                backgroundImage: 'url(/images/3D_banner_1.webp)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            }}
        >
            {/* Overlay com cor fixa */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)', // Sempre escuro
                    zIndex: 1,
                }}
            />

            {/* Conteúdo do login */}
            <Paper
                elevation={3}
                sx={{
                    zIndex: 2,
                    padding: 4,
                    width: '90%',
                    maxWidth: 500, // Mais amplo
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                }}
            >
                <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Box
                        component="img"
                        src="/images/logo-acompanha-transp.png"
                        alt="Acompanha Logo"
                        sx={{ width: '180px', height: 'auto', display: 'inline-block' }}
                    />
                </Typography>
                <form onSubmit={handleLogin}>
                    <TextField
                        label="Email"
                        type="email"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        sx={{
                            backgroundColor: '#f9f9f9',
                            '& .MuiInputLabel-root': { color: '#666666' },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#cccccc' },
                                '&:hover fieldset': { borderColor: '#999999' },
                            },
                        }}
                    />
                    <TextField
                        label="Senha"
                        type="password"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        sx={{
                            backgroundColor: '#f9f9f9',
                            '& .MuiInputLabel-root': { color: '#666666' },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#cccccc' },
                                '&:hover fieldset': { borderColor: '#999999' },
                            },
                        }}
                    />

                    {error && (
                        <Typography color="error" variant="body2" gutterBottom>
                            {error}
                        </Typography>
                    )}

                    {/* Checkbox "Lembrar de mim" */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                name="rememberMe"
                                color="primary"
                            />
                        }
                        label="Permanecer logado"
                        sx={{ marginTop: 1 }}
                    />
                    <br />
                    <small>Sua sessão ficará ativa por mais tempo.</small>

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        onClick={handleLogin}
                        disabled={isSubmitting} // Desativa o botão durante o login
                        sx={{
                            marginTop: 2,
                            background: isSubmitting
                                ? 'linear-gradient(to right, #6c757d, #495057)' // Cor diferenciada durante o processo
                                : 'linear-gradient(to right, #007bff, #0056b3)',
                            color: '#fff',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            padding: '10px 0',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                            '&:hover': {
                                background: isSubmitting
                                    ? 'linear-gradient(to right, #6c757d, #495057)' // Mantém a cor durante hover se estiver desabilitado
                                    : 'linear-gradient(to right, #0056b3, #003d7a)',
                                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
                            },
                        }}
                    >
                        {isSubmitting ? 'Entrando...' : 'Entrar'}
                    </Button>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                            marginTop: 2,
                        }}
                    >
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => navigate('/admin/forgot-password')}
                            sx={{
                                color: '#007bff',
                                textDecoration: 'underline',
                                '&:hover': {
                                    color: '#0056b3',
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            Esqueci Minha Senha
                        </Link>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => navigate('/')}
                            sx={{
                                color: '#007bff',
                                textDecoration: 'underline',
                                '&:hover': {
                                    color: '#0056b3',
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            Voltar ao Site
                        </Link>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                            marginTop: 2,
                        }}
                    >
                        <Button
                            onClick={() => setIsRegistrationModalOpen(true)}
                            variant="outlined"
                            sx={{
                                flex: 1,
                                color: '#007bff',
                                borderColor: '#007bff',
                                '&:hover': {
                                    backgroundColor: '#f0f8ff',
                                    borderColor: '#0056b3',
                                },
                            }}
                        >
                            Realizar Cadastro
                        </Button>
                        </Box>
                </form>
            </Paper>
            
            {/* Modal de Cadastro */}
            <RegistrationModal 
                open={isRegistrationModalOpen}
                onClose={() => setIsRegistrationModalOpen(false)}
            />
        </Box>
    );
}

export default LoginPage;
